
import React, { useState, useMemo, useEffect } from 'react';
import { Column, ColumnInstance, useTable } from 'react-table';
import { AllWellsTableType } from './AllWellsTableType';
import './AllWellsTable.scss';
import { ReactComponent as SortIconDefault } from '../../../../../../images/Chevron-default-icon.svg';
import { ReactComponent as SortIconUp } from '../../../../../../images/Chevron-up-icon.svg';
import { ReactComponent as SortIconDown } from '../../../../../../images/Chevron-down-icon.svg';
import { ReactComponent as StarIconEmpty } from '../../../../../../images/star-empty.svg';
import { ReactComponent as StarIconGold } from '../../../../../../images/star-gold.svg';
import { ReactComponent as ArrowUp } from '../../../../../../images/arrow-narrow-up.svg';
import UpTimeChart from '../all-wells-chart/UpTimeChart';
import IntakePressureChart from '../all-wells-chart/IntakePressureChart';
import ProductionChart from '../all-wells-chart/ProductionChart';
import WellWatchlistModal from '../wellWatchlistModal/wellWatchlistModal';
import thinArrow from '../../../../../../images/thinArrow.svg';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../../hooks/storeHooks';
import { addAssetToWatchList, fetchAssetList, removeAssetFromWatchList } from '../AllWellSlice';
import { toast } from 'react-toastify';
import { AssetData } from '../../../interfaces/AssetList.interface';
import Loader from '../../../../../common/page-loader/ComponentLoader';
import { WellWatchList } from '../../../interfaces/ControlRoomWells.interface';
import { getDefaultWatchList, updateWatchListById } from '../../ControlRoomWellsSlice';

interface AllWellsTableProps {
  toggleModal: (modalType: 'watchlist' | 'filters', isOpen: boolean) => void;
  selectedWatchList: WellWatchList;
}

const AllWellsTable = ({ toggleModal, selectedWatchList }: AllWellsTableProps) => {
  const navigate = useNavigate();
  const assetList = useAppSelector((state) => state.AssetList.AllWellAssetList).results as unknown as AllWellsTableType[];
  const assetListData = useAppSelector((state) => state.AssetList.AllWellAssetList) as unknown as AssetData;
  const assetListLoading = useAppSelector((state) => state.AssetList.loading);
  const [tableData, setTableData] = useState<AllWellsTableType[]>(assetList);
  const [isContextMenuOpen, setIsContextMenuOpen] = useState<number | null>(null);
  const [sortConfig, setSortConfig] = useState<{ key: keyof AllWellsTableType; direction: 'asc' | 'desc' } | null>({
    key: 'healthScore',
    direction: 'asc',
  });
  const [selectedWell, setSelectedWell] = useState('');
  const [selectedIntakeOption, setSelectedIntakeOption] = useState('Intake Pressure');
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const dispatch = useAppDispatch();
  const wellTableRef = React.createRef<HTMLDivElement>();
  const defaultWatchList = useAppSelector(getDefaultWatchList);

  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {

      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement
        if (
          scrollHeight - scrollTop - clientHeight < 100 &&
          !assetListLoading && assetListData.hasNextPage
        ) {
          dispatch(fetchAssetList({ pageSize: 25, pageNumber: assetListData.nextPageNumber || assetListData.pageNumber + 1 }));
        }
      }
    },
    [assetList?.length, assetListLoading, dispatch]
  )


  const selectOptions = ['Intake Pressure', 'Production'];

  const toggleDropdwon = () => {
    setIsSelectOpen(!isSelectOpen);
  };

  const handleItemClick = (item: string) => {
    setSelectedIntakeOption(item);
    setIsSelectOpen(!isSelectOpen);
  };

  const handleCloseModal = () => {
    setSelectedWell('');
  };

  const createBtnHandler = () => {
    setSelectedWell('');
    toggleModal('filters', true);
  };
  // Toggle star status for a particular row
  const handleStarClick = async (wellName: string, isStared: boolean) => {

    let response;
    if (isStared) {
      response = await dispatch(removeAssetFromWatchList(wellName));
    } else {
      response = await dispatch(addAssetToWatchList(wellName));
    }

    if (response.meta.requestStatus === 'fulfilled') {
      toast.success(isStared ? 'Well removed from watchlist' : 'Well added to watchlist');
      setTableData((prevData) =>
        prevData.map((item) => (item.wellName === wellName ? { ...item, isStared: !item.isStared } : item)),
      );
      dispatch(updateWatchListById({ id: defaultWatchList?.id ?? '', count: (defaultWatchList?.count ?? 0) + (isStared ? -1 : 1) } as WellWatchList));
    }


    // setSelectedWell(wellName);
    // setTableData((prevData) =>
    //   prevData.map((item) => (item.wellName === wellName ? { ...item, isStared: !item.isStared } : item)),
    // );

  };

  useEffect(() => {
    setTableData(assetList);
  }, [assetList]);


  const columns: Column<AllWellsTableType>[] = useMemo(
    () => [
      ...(!selectedWatchList.id ? [{
        Header: '',
        accessor: 'isStared' as keyof AllWellsTableType,
        width: 50,
        minWidth: 50,
        maxWidth: 50,
      }] : []),
      {
        Header: 'Well Name',
        accessor: 'wellName' as keyof AllWellsTableType,
        width: 200,
        minWidth: 200,
        maxWidth: 200,
      },
      {
        Header: 'HealthScore',
        accessor: 'healthScore' as keyof AllWellsTableType,
        width: 100,
        minWidth: 100,
        maxWidth: 100,
      },
      {
        Header: 'Operator',
        accessor: 'operator' as keyof AllWellsTableType,
        width: 150,
        minWidth: 150,
        maxWidth: 150,
      },
      {
        Header: 'Uptime',
        accessor: 'uptime' as keyof AllWellsTableType,
        width: 300,
        minWidth: 300,
        maxWidth: 300,
      },
      {
        Header: () => (
          <div>
            <div className='intake-pressure__dropdown'>
              <div className='intake-pressure__dropdown-input-container' onClick={toggleDropdwon}>
                <input type='text' className='intake-pressure__dropdown-input' value={selectedIntakeOption} readOnly />
                <img
                  src={thinArrow}
                  alt={isSelectOpen ? 'Up Arrow' : 'Down Arrow'}
                  className={`mr-3 ${isSelectOpen ? 'rotate-180' : ''}`}
                />
              </div>
              {isSelectOpen && (
                <ul className='intake-pressure__dropdown-list'>
                  {selectOptions.map((item, index) => (
                    <li
                      key={index}
                      onClick={() => handleItemClick(item)}
                      className='intake-pressure__dropdown-item'
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ),
        accessor: 'intakePressure' as keyof AllWellsTableType,
        width: 300,
        minWidth: 300,
        maxWidth: 300,
      },
      {
        Header: '7 Day Changes',
        accessor: 'sevenDaysChangeTrend' as keyof AllWellsTableType,
        width: 100,
        minWidth: 100,
        maxWidth: 100,
      },
      {
        Header: '',
        accessor: 'dropdown' as keyof AllWellsTableType,
        width: 50,
        minWidth: 50,
        maxWidth: 50,
      },
    ],
    [tableData, selectedIntakeOption, isSelectOpen],
  );



  const sortedData = useMemo(() => {
    if (assetListLoading) {
      return [];
    }
    if (sortConfig === null) return tableData;
    return tableData && [...tableData]?.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue == null || bValue == null) return 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue) * (sortConfig.direction === 'asc' ? 1 : -1);
      }
      return aValue < bValue
        ? sortConfig.direction === 'asc'
          ? -1
          : 1
        : aValue > bValue
          ? sortConfig.direction === 'asc'
            ? 1
            : -1
          : 0;
    });
  }, [tableData, sortConfig]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns: columns || [],
    data: sortedData || [],

  });

  const handleSort = (column: ColumnInstance<AllWellsTableType>) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === column.id && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: column.id as keyof AllWellsTableType, direction });
  };

  const openContextMenuOpen = (index: number) => {
    setIsContextMenuOpen((prevIndex: any) => (prevIndex === index ? null : index));
  };

  const getBorderColor = (score: any) => {
    if (score < 50) return '#F97066';
    if (score >= 51 && score <= 75) return '#FFBF3F';
    if (score > 75) return '#12B76A ';
    return '#CCC';
  };

  const handleWellNameClick = (selectedWell: any) => {
    const selectedAsset = {
      assetName: selectedWell.wellName,
      assetGroupName: 'Well Groups',
      assetId: selectedWell.id,
      searchString: undefined,
    };
    if (selectedAsset) {
      sessionStorage.setItem('selectedWellName', JSON.stringify(selectedAsset));
    }
    navigate(`/layout/well-control-room`);

  }

  interface RenderCellContentProps {
    cell: any;
    row: any;
  }

  const renderCellContent = ({ cell, row }: RenderCellContentProps) => {
    const { id } = cell.column;
    const { original } = row;

    switch (id) {
      case 'isStared':
        if (!selectedWatchList.id) {
          return (
            <span
              className="favourite-icon"
              onClick={() => handleStarClick(original.wellName, original.isStared)}
            >
              {original.isStared ? <StarIconGold /> : <StarIconEmpty />}
            </span>
          );
        }
        return null;

      case 'wellName':
        return (
          <div onClick={() => handleWellNameClick(original)} className="well-name-link">
            {cell.render('Cell')}
          </div>
        );

      case 'healthScore':
        return (
          <div className="flex justify-left align-center">
            <div
              className="health-score-circle flex justify-center align-center items-center"
              style={{ border: `2px solid ${getBorderColor(original.healthScore)}` }}
            >
              {original.healthScore}
            </div>
          </div>
        );

      case 'uptime':
        return <UpTimeChart data={original.uptimeData || []} />;

      case 'intakePressure':
        return selectedIntakeOption === 'Intake Pressure' ? (
          <IntakePressureChart data={original.intakePressureData || []} />
        ) : (
          <ProductionChart data={original.productionData || []} />
        );

      case 'sevenDaysChangeTrend':
        return (
          <div className="all-wells-day-change">
            <div className="square-bracket-style">
              <ArrowUp /> {original.sevenDaysChangeTrend}%
            </div>
          </div>
        );

      case 'dropdown':
        return (
          <div className="dropdown-container">
            <button className="dropdown-btn" onClick={() => openContextMenuOpen(row.index)}>
              <span className="dot"></span>
              <span className="dot"></span>
              <span className="dot"></span>
            </button>
            {isContextMenuOpen === row.index && (
              <div className="dropdown-options">
                <button className="dropdown-option show-element">System dashboard snapshot</button>
                <button className="dropdown-option show-element">Remove well from watchlist</button>
              </div>
            )}
          </div>
        );

      default:
        return cell.render('Cell');
    }
  };

  return (
    <>
      <div className='doc-wrapper multiwell-table-wrapper'>
        <div className='table-container w-full !rounded-[8px]'
          ref={wellTableRef}
          onScroll={() => fetchMoreOnBottomReached(wellTableRef.current)}
        >
          <table className='document-table' {...getTableProps()} key='table'>
            <thead className='document-head'>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id + 'header'}>
                  {headerGroup.headers.map((column) => (
                    <th
                      {...column.getHeaderProps({
                        style: { width: column.width, minWidth: column.minWidth, maxWidth: column.maxWidth },
                      })}
                      onClick={() => {
                        if (column.id !== 'uptime' && column.id !== 'intakePressure' && column.id !== 'dropdown') {
                          handleSort(column as ColumnInstance<AllWellsTableType>)
                        }
                      }
                      }
                      key={column.id}
                    >
                      <div>
                        {column.render('Header')}
                        {(column.id !== 'uptime' && column.id !== 'intakePressure' && column.id !== 'dropdown') ?
                          sortConfig?.key === column.id ? (
                            sortConfig.direction === 'asc' ? (
                              <SortIconUp alt='sort-asc' className='sort-img' />
                            ) : (
                              <SortIconDown alt='sort-dsc' className='sort-img' />
                            )
                          ) : (
                            <SortIconDefault alt='sort-asc' className='sort-img sort-img-show-hover' />
                          ) : ''}
                      </div>
                    </th>
                  ))}
                  {/* <th></th> */}
                </tr>
              ))}
            </thead>
            {
              assetListLoading && <Loader />
            }
            <tbody className={`document-body ${assetListLoading ? "blur-background pointer-events-none" : ""}`} {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={row.id}>
                    {row.cells.map((cell) => (
                      <td
                        {...cell.getCellProps({
                          style: {
                            width: cell.column.width,
                            minWidth: cell.column.minWidth,
                            maxWidth: cell.column.maxWidth,
                          },
                        })}
                        key={cell.column.id}
                      >
                        {renderCellContent({ cell, row })}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selectedWell && <WellWatchlistModal handleCloseModal={handleCloseModal} createBtnHandler={createBtnHandler} />}
    </>
  );
};

export default AllWellsTable;